import {css, StyleSheet} from "aphrodite";
import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";
import {ImageSource} from "@khan/guide-types";

import {
    PADDING_TO_REVEAL_BORDER,
    PREVIEW_HEIGHT,
} from "../modal-content/constants.ts";
import {convertImageToDataURL} from "../utils/convert-image-to-data-url.ts";

/**
 * A component that supports a user capturing a still frame from a camera.
 */
export interface CameraCaptureProps {
    onCapture: (dataURL: string, source: ImageSource.ProvidedByCamera) => void;
    showCamera: boolean;
    onCancel: () => void;
    onError: () => void;
}

export const CameraCapture = forwardRef<
    CameraCaptureHandle,
    CameraCaptureProps
>(function CameraCapture({onCapture, showCamera, onCancel, onError}, ref) {
    const [error, setError] = useState<string | null>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const streamRef = useRef<MediaStream | null>(null);

    useImperativeHandle(ref, () => ({
        triggerCapture: handleCapture,
    }));

    useEffect(() => {
        if (showCamera) {
            setError(null);
            (async () => {
                try {
                    const stream = await navigator.mediaDevices.getUserMedia({
                        video: {
                            // Use rear camera if available
                            facingMode: "environment",
                        },
                    });
                    if (videoRef.current) {
                        videoRef.current.srcObject = stream;
                        videoRef.current.play();
                    }
                    streamRef.current = stream;
                } catch (err) {
                    setError(
                        "Unable to access camera. Please check your permissions.",
                    );
                    onError();
                    onCancel();
                }
            })();
        }
        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => track.stop());
                streamRef.current = null;
            }
        };
    }, [showCamera, onCancel, onError]);

    function handleCapture() {
        if (!videoRef.current) {
            return;
        }
        const video = videoRef.current;

        try {
            // Use shared utility to scale and convert to PNG
            const dataURL = convertImageToDataURL(video);
            onCapture(dataURL, ImageSource.ProvidedByCamera);
        } catch (error) {
            onError();
        }
    }

    if (!showCamera) {
        return null;
    }

    return (
        <View style={styles.container}>
            {error ? (
                <View style={styles.error}>{error}</View>
            ) : (
                <video
                    ref={videoRef}
                    data-testid="camera-video"
                    className={css(styles.video)}
                    autoPlay
                    playsInline
                >
                    <track kind="captions" />
                </video>
            )}
        </View>
    );
});

const styles = StyleSheet.create({
    container: {
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: PREVIEW_HEIGHT + 2 * PADDING_TO_REVEAL_BORDER,
    },
    video: {
        // Without the minBlockSize, the video intially renders slightly too small!
        minBlockSize: PREVIEW_HEIGHT,
        background: semanticColor.core.background.neutral.strong,
    },
    error: {
        color: semanticColor.core.foreground.critical.default,
        paddingBlock: spacing.medium_16,
    },
});

export interface CameraCaptureHandle {
    triggerCapture: () => void;
}
